import React, { useEffect, useState } from 'react';
import { Drawer } from './ui/Drawer';
import { Loader2, Truck, Package, AlertCircle } from 'lucide-react';
import Toggle from './Toggle';
import api from '../services/api';
import { DriverLane } from './driver-status-monitor';
import type { DriverWithMoves } from './driver-status-monitor';
import { DEFAULT_DISPLAY_SETTINGS } from './driver-status-monitor';
import FollowUpBookmark from './FollowUpBookmark';
import { driverFollowUp } from '../utils/followUpLabels';

interface DriverLoadsDrawerV2Props {
  isOpen: boolean;
  onClose: () => void;
  unitNumber: string | null;
  driverName?: string;
  /** PRO the drawer was opened from (e.g. the Pro drawer) — its moves get a highlighted PRO link */
  highlightPronumber?: number;
}

export const DriverLoadsDrawerV2: React.FC<DriverLoadsDrawerV2Props> = ({
  isOpen,
  onClose,
  unitNumber,
  driverName,
  highlightPronumber,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DriverWithMoves | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);

  useEffect(() => {
    if (isOpen && unitNumber) {
      fetchDriverMoves();
    } else if (!isOpen) {
      setData(null);
      setError(null);
    }
  }, [isOpen, unitNumber]);

  const fetchDriverMoves = async () => {
    if (!unitNumber) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/driver-status-monitor/drivers/${unitNumber}`);
      setData(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('No moves found for this driver today');
      } else {
        setError(err.response?.data?.detail || 'Failed to fetch driver moves');
      }
    } finally {
      setLoading(false);
    }
  };

  // Count completed moves for display
  const completedCount = data?.moves.filter(m => m.status === 'Completed').length ?? 0;

  // Count of non-completed moves
  const activeAndPendingCount = data?.moves.filter(m => m.status !== 'Completed').length ?? 0;

  // Get working PRO (first In Transit/At Origin/At Destination move)
  const workingMove = data?.moves.find(m =>
    m.status === 'In Transit' || m.status === 'At Origin' || m.status === 'At Destination'
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-muted" />
          <span>
            Driver {unitNumber}
            {(driverName || data?.driver_name) && (
              <span className="text-muted font-normal"> - {driverName || data?.driver_name}</span>
            )}
          </span>
          {/* This drawer IS the driver detail surface, and the Follow Up panel
              reopens it by mounting this same component -- so the bookmark
              belongs here rather than on the Drivers page (whose DriverDrawer is
              a dispatch picker with nothing to reopen). */}
          {(() => {
            const bookmark = driverFollowUp(unitNumber, driverName || data?.driver_name);
            return bookmark ? (
              <FollowUpBookmark
                type={bookmark.type}
                itemId={bookmark.itemId}
                label={bookmark.label}
              />
            ) : null;
          })()}
        </div>
      }
      width="50%"
    >
      <div className="p-4">
        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-3" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Loading driver moves...</p>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900 dark:text-red-100">Error loading driver moves</p>
                <p className="text-xs text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && data && data.moves.length === 0 && (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No current moves assigned to this driver
            </p>
          </div>
        )}

        {/* Driver Status Summary with Toggle */}
        {!loading && !error && data && data.moves.length > 0 && (
          <>
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {activeAndPendingCount} active move{activeAndPendingCount !== 1 ? 's' : ''}
                    {completedCount > 0 && !showCompleted && (
                      <span className="text-gray-400"> ({completedCount} completed)</span>
                    )}
                  </span>
                  {/* Show Completed Toggle */}
                  {completedCount > 0 && (
                    <div className="flex items-center gap-2">
                      <Toggle
                        checked={showCompleted}
                        onChange={setShowCompleted}
                        size="sm"
                      />
                      <span className="text-xs text-muted">Show Completed</span>
                    </div>
                  )}
                </div>
                {workingMove && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                    Working: PRO #{workingMove.pronumber}
                  </span>
                )}
                {!workingMove && activeAndPendingCount > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                    Not currently working
                  </span>
                )}
              </div>
            </div>

            {/* Move Cards — delegated to shared DriverLane component */}
            <DriverLane
              driver={data}
              hideHeader
              showCompleted={showCompleted}
              displaySettings={DEFAULT_DISPLAY_SETTINGS}
              orientation="vertical"
              highlightPronumber={highlightPronumber}
            />
          </>
        )}
      </div>
    </Drawer>
  );
};

export default DriverLoadsDrawerV2;
